/**
 * Nado gateway client: the queries and the withdraw execute used by the
 * withdrawal flow.
 *
 * Flow composition: `max_withdrawable` / `fee_rates` feed the enter-amount
 * screen → `nonces` immediately before signing → execute
 * `withdraw_collateral_v2` with `sendTo` = the Fun deposit address → checkout
 * tracking takes over once funds arrive.
 *
 * @see {@link https://docs.nado.xyz/developer-resources/api/gateway | Nado gateway API}
 */

import type { Address, Hex } from 'viem'
import type { FunLogger } from '../../src/utils/funLogger'
import { NADO_GATEWAY_URL } from './consts'
import {
  NadoGatewayError,
  NadoWithdrawalUnconfirmedError,
  createNadoGatewayError,
  isNadoTransportError,
  parseNadoExpectedNonce,
} from './errors'
import { type NadoRequestOptions, nadoFetch } from './fetch'

interface NadoGatewayResponse<T> {
  status: 'success' | 'failure'
  data?: T
  error?: string
  error_code?: number
}

/**
 * Nado returns HTTP 200 with `status: 'failure'` for logical errors; those
 * map to NadoGatewayError. Retries live in the react-query hooks — the one
 * exception is getNadoTxNonce's single transport retry below.
 */
function parseNadoGatewayResponse<T>(
  response: NadoGatewayResponse<T>,
  uri: string,
  logger: FunLogger,
): T {
  if (response.status !== 'success') {
    logger.warn('nado:gateway_rejected', {
      uri,
      error: response.error,
      errorCode: response.error_code,
    })
    throw createNadoGatewayError(response, 'Nado gateway request failed')
  }
  return response.data as T
}

/**
 * Queries use GET with query params, matching Nado's docs — the URL in a log
 * line reproduces the query directly.
 */
async function nadoQuery<T>(
  params: Record<string, string>,
  options: NadoRequestOptions,
): Promise<T> {
  const uri = `${NADO_GATEWAY_URL}/query?${new URLSearchParams(params)}`
  const response = await nadoFetch<NadoGatewayResponse<T>>(
    { uri, method: 'GET' },
    options,
  )
  const data = parseNadoGatewayResponse(response, uri, options.logger)
  // A success response without data would TypeError in every caller
  if (data === undefined) {
    throw new NadoGatewayError('Nado returned success without data', {
      kind: 'api',
    })
  }
  return data
}

/**
 * Largest withdrawal (x18) the subaccount can make, measured against prod:
 * `(spot_leverage ? initial_health : min(spot, initial_health)) − sequencer_fee`
 *
 * - Already net of the fee: `max + fee` is debited, `max` is what arrives — so
 *   the screen adds it back (`withdrawalForm.ts`).
 * - Health-bound, so no sum of spot balances can replace it: an open perp cuts
 *   the max without moving any balance.
 */
export async function getNadoMaxWithdrawable(
  params: { productId: number; sender: Hex; spotLeverage: boolean },
  options: NadoRequestOptions,
): Promise<string> {
  const data = await nadoQuery<{ max_withdrawable: string }>(
    {
      type: 'max_withdrawable',
      product_id: params.productId.toString(),
      sender: params.sender,
      spot_leverage: params.spotLeverage ? 'true' : 'false',
    },
    options,
  )
  return data.max_withdrawable
}

/**
 * Current `tx_nonce` for the owner wallet — fetch immediately before signing.
 * One manual retry on network failures only (imperative call site, no
 * react-query retry layer above it).
 */
export async function getNadoTxNonce(
  address: Address,
  options: NadoRequestOptions,
): Promise<bigint> {
  const query = () =>
    nadoQuery<{ tx_nonce: string }>({ type: 'nonces', address }, options)
  let data: { tx_nonce: string }
  try {
    data = await query()
  } catch (error) {
    // Api-kind and HTTP failures mean Nado answered — don't retry
    if (!isNadoTransportError(error)) {
      throw error
    }
    data = await query()
  }
  return BigInt(data.tx_nonce)
}

export interface NadoFeeRates {
  /** Withdraw sequencer fee per product (indexed by product_id), x18 token units. */
  withdraw_sequencer_fees: string[]
}

export async function getNadoFeeRates(
  sender: Hex,
  options: NadoRequestOptions,
): Promise<NadoFeeRates> {
  return nadoQuery<NadoFeeRates>({ type: 'fee_rates', sender }, options)
}

export interface ExecuteWithdrawCollateralV2Params {
  /** Subaccount id per Nado's spec (see `buildNadoSubaccountId`). */
  sender: Hex
  productId: number
  /** Raw base units in the token's native decimals — not x18. */
  amount: bigint
  nonce: bigint
  sendTo: Address
  signature: Hex
  /** Required so callers can't enable borrowing by omission — `false` fails
   * the withdrawal instead of opening a borrow (Nado defaults absent to true). */
  spotLeverage: boolean
}

/**
 * Submit a signed `withdraw_collateral_v2`; resolves on gateway acceptance,
 * on-chain settlement follows asynchronously.
 *
 * A timeout means "no answer", not "didn't happen", so it replays the
 * identical payload rather than surfacing an ambiguous failure. `tx_nonce` is
 * a sequential counter, so the replay either fills the same slot or is
 * rejected — it can never withdraw twice.
 */
export async function executeNadoWithdrawCollateralV2(
  params: ExecuteWithdrawCollateralV2Params,
  options: NadoRequestOptions,
): Promise<void> {
  const uri = `${NADO_GATEWAY_URL}/execute`
  const body = {
    // spot_leverage sits INSIDE the execute object (sibling of tx/signature),
    // matching @nadohq's EngineBaseClient — top-level is silently ignored.
    withdraw_collateral_v2: {
      tx: {
        sender: params.sender,
        productId: params.productId,
        amount: params.amount.toString(),
        nonce: params.nonce.toString(),
        sendTo: params.sendTo,
        appendix: '0',
      },
      signature: params.signature,
      spot_leverage: params.spotLeverage,
    },
  }

  try {
    const response = await nadoFetch<NadoGatewayResponse<never>>(
      { uri, method: 'POST', body },
      options,
    )
    parseNadoGatewayResponse(response, uri, options.logger)
  } catch (error) {
    // Only an unanswered request is ambiguous; a rejection already told us
    // the withdrawal didn't happen.
    if (!isNadoUnansweredError(error)) {
      throw error
    }
    options.logger.warn('nado:execute_unanswered', {
      uri,
      nonce: params.nonce.toString(),
      productId: params.productId,
      errorKind: error instanceof NadoGatewayError ? error.kind : null,
    })
    await replayNadoWithdrawExecute({ uri, body, nonce: params.nonce }, options)
  }
}

/** No reply reached us, so the withdrawal's fate is unknown. */
function isNadoUnansweredError(error: unknown): boolean {
  return (
    error instanceof NadoGatewayError &&
    (error.kind === 'timeout' || error.kind === 'transport')
  )
}

/**
 * Re-submits the byte-identical payload — same nonce, same signature, so no
 * second wallet prompt. Resolves when the withdrawal is confirmed landed,
 * whether by this attempt or the one that went unanswered.
 */
async function replayNadoWithdrawExecute(
  {
    uri,
    body,
    nonce,
  }: {
    uri: string
    body: object
    nonce: bigint
  },
  options: NadoRequestOptions,
): Promise<void> {
  const { logger } = options
  try {
    const response = await nadoFetch<NadoGatewayResponse<never>>(
      { uri, method: 'POST', body },
      options,
    )
    parseNadoGatewayResponse(response, uri, logger)
    logger.warn('nado:execute_replay_accepted', {
      uri,
      nonce: nonce.toString(),
    })
  } catch (error) {
    const expectedNonce = parseNadoExpectedNonce(error)
    // Nado moved past our nonce, so the unanswered attempt is what consumed
    // it — the withdrawal landed.
    if (expectedNonce !== undefined && expectedNonce > nonce) {
      logger.warn('nado:execute_replay_alreadyLanded', {
        uri,
        nonce: nonce.toString(),
        expectedNonce: expectedNonce.toString(),
      })
      return
    }
    logger.error(
      'nado:execute_replay_unconfirmed',
      error instanceof Error ? error : new Error(String(error)),
      {
        uri,
        nonce: nonce.toString(),
        expectedNonce: expectedNonce?.toString() ?? null,
        errorMessage: error instanceof Error ? error.message : String(error),
      },
    )
    throw new NadoWithdrawalUnconfirmedError()
  }
}
